<template>
    <BaseLoader :loader="loader" />


  <div class="admin-container">
    <NaviPanel />

    <main class="main-layout">
      <NaviHeader />

      <div class="content-scroll">
        <div class="cs-card">
            <h3>Reports</h3>
            <p class="text-tooltip">Generate and analyze various reports based on campaign data, streamer performance, and delivery metrics.</p>

            <button class="spec-btn refresh-btn" @click="refreshData" title="Refresh">
                <app-icon icon="hugeicons:refresh-dot" width="18" />
            </button>
         </div>

         <!-- Report Type Selector (Tabs) -->
         <div class="cs-card cs-animation">
            <div class="tab-navigation">
                <button 
                    class="tab-button" 
                    :class="{ active: activeTab === 'campaign-performance' }"
                    @click="selectReport('campaign-performance')"
                >
                    <app-icon icon="hugeicons:analytics-02" width="18" />
                    <span>Campaign Performance</span>
                </button>
                <button 
                    class="tab-button" 
                    :class="{ active: activeTab === 'streamer-stats' }"
                    @click="selectReport('streamer-stats')"
                >
                    <app-icon icon="hugeicons:user-multiple-02" width="18" />
                    <span>Streamer Statistics</span>
                </button>
                <button 
                    class="tab-button" 
                    :class="{ active: activeTab === 'format-analysis' }"
                    @click="selectReport('format-analysis')"
                >
                    <app-icon icon="hugeicons:image-composition" width="18" />
                    <span>Format Analysis</span>
                </button>
                <button 
                    class="tab-button" 
                    :class="{ active: activeTab === 'delivery-report' }"
                    @click="selectReport('delivery-report')"
                >
                    <app-icon icon="hugeicons:package-delivered" width="18" />
                    <span>Delivery Report</span>
                </button>
                <button 
                    class="tab-button" 
                    :class="{ active: activeTab === 'financial-summary' }"
                    @click="selectReport('financial-summary')"
                >
                    <app-icon icon="hugeicons:money-bag-02" width="18" />
                    <span>Financial Summary</span>
                </button>
            </div>

            <!-- Report Parameters and Content -->
            <div class="report-content">
                
                <!-- Campaign Performance Report -->
                <div v-show="activeTab === 'campaign-performance'">
                    <div class="report-header">
                        <div class="report-title">
                            <app-icon icon="hugeicons:analytics-02" width="24" />
                            <h4>Campaign Performance Report</h4>
                        </div>
                        <p class="report-description">Detailed analysis of campaign metrics including views, engagement, and target achievement.</p>
                    </div>

                    <div class="report-parameters">
                        <div class="form-row">
                            <div class="form-group">
                                <label class="form-label">Date From</label>
                                <input type="date" v-model="params.campaignPerformance.dateFrom" class="form-input">
                            </div>
                            <div class="form-group">
                                <label class="form-label">Date To</label>
                                <input type="date" v-model="params.campaignPerformance.dateTo" class="form-input">
                            </div>
                        </div>
                        <div class="form-row">
                            <div class="form-group form-group-full">
                                <label class="form-label">Campaign Status</label>
                                <select v-model="params.campaignPerformance.status" class="form-input">
                                    <option value="all">All Campaigns</option>
                                    <option value="active">Active Only</option>
                                    <option value="completed">Completed</option>
                                    <option value="scheduled">Scheduled</option>
                                </select>
                            </div>
                        </div>
                        <button @click="generateReport('campaign-performance')" class="generate-btn">
                            <app-icon icon="hugeicons:file-download" width="18" />
                            <span>Generate Report</span>
                        </button>
                    </div>

                    <div v-if="reportResults.campaignPerformance" class="report-results">
                        <div class="results-placeholder">
                            <app-icon icon="hugeicons:chart-line-data-03" width="48" />
                            <p>Report results will be displayed here</p>
                        </div>
                    </div>
                </div>

                <!-- Streamer Statistics Report -->
                <div v-show="activeTab === 'streamer-stats'" class="report-section">
                    <div class="report-header">
                        <div class="report-title">
                            <app-icon icon="hugeicons:user-multiple-02" width="24" />
                            <h4>Streamer Statistics Report</h4>
                        </div>
                        <p class="report-description">Comprehensive statistics about streamer performance, viewer counts, and engagement metrics.</p>
                    </div>

                    <div class="report-parameters">
                        <div class="form-row">
                            <div class="form-group">
                                <label class="form-label">Period</label>
                                <select v-model="params.streamerStats.period" class="form-input">
                                    <option value="today">Today</option>
                                    <option value="week">Last 7 Days</option>
                                    <option value="month">Last 30 Days</option>
                                    <option value="custom">Custom Range</option>
                                </select>
                            </div>
                            <div class="form-group">
                                <label class="form-label">Minimum Viewers</label>
                                <input type="number" v-model="params.streamerStats.minViewers" class="form-input" placeholder="0">
                            </div>
                        </div>
                        <div class="form-row">
                            <div class="form-group form-group-full">
                                <label class="form-label">Live Status</label>
                                <select v-model="params.streamerStats.liveStatus" class="form-input">
                                    <option value="all">All Streamers</option>
                                    <option value="live">Currently Live</option>
                                    <option value="offline">Offline</option>
                                </select>
                            </div>
                        </div>
                        <button @click="generateReport('streamer-stats')" class="generate-btn">
                            <app-icon icon="hugeicons:file-download" width="18" />
                            <span>Generate Report</span>
                        </button>
                    </div>

                    <div v-if="reportResults.streamerStats" class="report-results">
                        <div class="results-placeholder">
                            <app-icon icon="hugeicons:chart-line-data-03" width="48" />
                            <p>Report results will be displayed here</p>
                        </div>
                    </div>
                </div>

                <!-- Format Analysis Report -->
                <div v-show="activeTab === 'format-analysis'" class="report-section">
                    <div class="report-header">
                        <div class="report-title">
                            <app-icon icon="hugeicons:image-composition" width="24" />
                            <h4>Format Analysis Report</h4>
                        </div>
                        <p class="report-description">Analysis of banner format performance, exposure time, and effectiveness across campaigns.</p>
                    </div>

                    <div class="report-parameters">
                        <div class="form-row">
                            <div class="form-group">
                                <label class="form-label">Date from</label>
                                <vue-datepicker 
                                    v-model="params.formatAnalysis.dateFrom"  
                                    placeholder="Select Date from"                      
                                    :teleport="true"
                                    auto-apply
                                    required
                                    :format="formatDateD"
                                    model-type="yyyy-MM-dd"
                                    :enable-time-picker="false"
                                    @update:model-value="refreshData"/>
                            </div>
                            <div class="form-group">
                                <label class="form-label">Date to</label>
                                <vue-datepicker 
                                    v-model="params.formatAnalysis.dateTo" 
                                    placeholder="Select Date to"                       
                                    :teleport="true"
                                    auto-apply
                                    required
                                    :format="formatDateD"
                                    model-type="yyyy-MM-dd"
                                    :enable-time-picker="false"
                                    @update:model-value="refreshData"
                                    :min-date="params.formatAnalysis.dateFrom" />
                            </div>
                            <div class="form-group">
                                <label class="form-label">Type</label>
                                <!-- <select v-model="params.formatAnalysis.reportType" class="form-input">
                                    <option value="campaigns">Campaigns</option>
                                    <option value="formats">Formats only</option>                                    
                                </select> -->

                                <vue-select
                                class="form-input-select form-input" 
                                    :key="selectKey" 
                                    v-model="params.formatAnalysis.reportType" 
                                    :options="formatDict" 
                                    label-by="name"
                                    placeholder="Select Type"
                                    value-by="id" 
                                    @update:modelValue="refreshData"                           
                                    clear-on-close 
                                    close-on-select  /> 

                            </div>
                        </div>
                        <button @click="generateReport('format-analysis')" class="generate-btn">
                            <app-icon icon="hugeicons:file-download" width="18" />
                            <span>Generate Report</span>
                        </button>
                    </div>

                    <div v-if="reportResults.formatAnalysis" class="report-results">
                        <vue-good-table
                            :columns="formatAnalysisColumns"
                            :rows="reportResults.formatAnalysis"
                            :line-numbers="false"
                            :search-options="{enabled: true}"
                            :sort-options="{enabled: true}"
                            :pagination-options="{
                                    enabled: true,
                                    mode: 'records'
                            }" 
                                        
                        >
                            <template v-slot:emptystate>
                            No data
                            </template>                                
                        </vue-good-table>

                        <div class="chart-section">
                            <BarChart 
                                :chartData="barFormatAnalysisData" 
                                :options="barFormatAnalysisOptions"
                                :height="280"
                            />
                        </div>
                    </div>
                </div>

                <!-- Delivery Report -->
                <div v-show="activeTab === 'delivery-report'" class="report-section">
                    <div class="report-header">
                        <div class="report-title">
                            <app-icon icon="hugeicons:package-delivered" width="24" />
                            <h4>Delivery Report</h4>
                        </div>
                        <p class="report-description">Track campaign delivery progress, target achievement, and real-time delivery metrics.</p>
                    </div>

                    <div class="report-parameters">
                        <div class="form-row">
                            <div class="form-group">
                                <label class="form-label">Campaign</label>
                                <!-- <select v-model="params.deliveryReport.campaignId" class="form-input" placeholder="Select Campaign">
                                    <option value="0">Select Campaign</option>
                                    <option v-for="campaign in campaignDict" :key="campaign.Id" :value="campaign.Id">
                                        {{ campaign.ClientCampaignName }}
                                    </option>
                                </select> -->

                                <vue-select 
                                    class="form-input-select form-input" 
                                    :key="selectKey" 
                                    v-model="params.deliveryReport.campaignId" 
                                    :options="campaignDict" 
                                    label-by="ClientCampaignName"
                                    placeholder="Select Campaign"
                                    value-by="Id" 
                                    @update:modelValue="refreshData"
                                    searchable                     
                                    clear-on-close 
                                    close-on-select  /> 


                            </div>
                        </div>
                        <button @click="generateReport('delivery-report')" class="generate-btn">
                            <app-icon icon="hugeicons:file-download" width="18" />
                            <span>Generate Report</span>
                        </button>
                    </div>

                    <div v-if="reportResults.deliveryReport" class="report-results">
                        <div class="report-section-header" v-if="reportResults.deliveryReportInfo">
                            <div class="rsh-title">
                                <app-icon icon="hugeicons:package-delivered" width="20" />
                                <h4>{{ reportResults.deliveryReportInfo.CampaignName }}</h4>
                                <span class="campaign-status-badge" :class="'status-' + reportResults.deliveryReportInfo.CampaignStatus?.toLowerCase()">
                                    {{ reportResults.deliveryReportInfo.CampaignStatus }}
                                </span>
                            </div>
                            <div class="rsh-meta">
                                <div class="rsh-item">
                                    <span class="rsh-label">Billing Model</span>
                                    <span class="rsh-value">{{ reportResults.deliveryReportInfo.BillingModel }}</span>
                                </div>
                                <div class="rsh-item">
                                    <span class="rsh-label">Date From</span>
                                    <span class="rsh-value">{{ reportResults.deliveryReportInfo.DateFrom?.split('T')[0] }}</span>
                                </div>
                                <div class="rsh-item">
                                    <span class="rsh-label">Date To</span>
                                    <span class="rsh-value">{{ reportResults.deliveryReportInfo.DateTo?.split('T')[0] }}</span>
                                </div>
                                <div class="rsh-item">
                                    <span class="rsh-label">Overall Budget <span class="l-case">(pln)</span></span>
                                    <span class="rsh-value">{{ reportResults.deliveryReportInfo.OverallBudget?.toLocaleString() }}</span>
                                </div>
                                <div class="rsh-item">
                                    <span class="rsh-label">Cost per View <span class="l-case">(gr)</span></span>
                                    <span class="rsh-value">{{ reportResults.deliveryReportInfo.CostPerView }}</span>
                                </div>
                                <div class="rsh-item" v-if="reportResults.deliveryReportInfo.ViewTime">
                                    <span class="rsh-label">View Time <span class="l-case">(s)</span></span>
                                    <span class="rsh-value">{{ reportResults.deliveryReportInfo.ViewTime }}</span>
                                </div>
                                <div class="rsh-item">
                                    <span class="rsh-label">Target Views <span class="l-case">(units)</span></span>
                                    <span class="rsh-value">{{ reportResults.deliveryReportInfo.TargetView?.toLocaleString() }}</span>
                                </div>
                            </div>
                        </div>
                        <div class="chart-section">
                            <LineChart
                                :chartData="lineDeliveryData"
                                :options="lineDeliveryOptions"
                                :plugins="todayLinePlugin"
                                :height="320"
                            />
                        </div>
                        <div class="unit-info text-tooltip">
                            <span class="l-case">Unit = 1 view * {{ reportResults.deliveryReportInfo.ViewTime }}s (View Time)</span>
                        </div>
                    </div>
                </div>

                <!-- Financial Summary Report -->
                <div v-show="activeTab === 'financial-summary'" class="report-section">
                    <div class="report-header">
                        <div class="report-title">
                            <app-icon icon="hugeicons:money-bag-02" width="24" />
                            <h4>Financial Summary Report</h4>
                        </div>
                        <p class="report-description">Financial overview including campaign costs, client billing, and budget tracking.</p>
                    </div>

                    <div class="report-parameters">
                        <div class="form-row">
                            <div class="form-group">
                                <label class="form-label">Client</label>
                                <select v-model="params.financialSummary.clientId" class="form-input">
                                    <option value="all">All Clients</option>
                                    <option value="1">Client 1</option>
                                    <option value="2">Client 2</option>
                                    <!-- Add more clients from API -->
                                </select>
                            </div>
                            <div class="form-group">
                                <label class="form-label">Fiscal Period</label>
                                <select v-model="params.financialSummary.fiscalPeriod" class="form-input">
                                    <option value="current-month">Current Month</option>
                                    <option value="last-month">Last Month</option>
                                    <option value="quarter">This Quarter</option>
                                    <option value="year">This Year</option>
                                </select>
                            </div>
                        </div>
                        <div class="form-row">
                            <div class="form-group form-group-full">
                                <label class="form-label">Include</label>
                                <div class="checkbox-group">
                                    <label class="checkbox-label">
                                        <input type="checkbox" v-model="params.financialSummary.includeActive">
                                        <span>Active Campaigns</span>
                                    </label>
                                    <label class="checkbox-label">
                                        <input type="checkbox" v-model="params.financialSummary.includeCompleted">
                                        <span>Completed Campaigns</span>
                                    </label>
                                    <label class="checkbox-label">
                                        <input type="checkbox" v-model="params.financialSummary.includePending">
                                        <span>Pending Invoices</span>
                                    </label>
                                </div>
                            </div>
                        </div>
                        <button @click="generateReport('financial-summary')" class="generate-btn">
                            <app-icon icon="hugeicons:file-download" width="18" />
                            <span>Generate Report</span>
                        </button>
                    </div>

                    <div v-if="reportResults.financialSummary" class="report-results">
                        <div class="results-placeholder">
                            <app-icon icon="hugeicons:chart-line-data-03" width="48" />
                            <p>Report results will be displayed here</p>
                        </div>
                    </div>
                </div>

            </div>
         </div>

      </div>
    </main>
  </div>
</template>


<script>
    import NaviPanel from '@/components/NaviPanel.vue';
    import NaviHeader from '@/components/NaviHeader.vue';
    import apiService from "@/services/other.js";
    import shared from "@/shared";
    import BaseLoader from "@/components/BaseLoader.vue";

    import { BarChart } from 'vue-chart-3';
    import { LineChart } from 'vue-chart-3';
    import { Chart, registerables } from "chart.js";

    Chart.register(...registerables);

    export default {
        components: { NaviPanel, NaviHeader, BarChart, LineChart, BaseLoader },
        name: 'ReportsView',

        data: function () {
            return {
                activeTab: 'campaign-performance',
                loader: false,
                selectKey: 0,
                campaignDict: [],
                formatDict: [
                    { id: 'campaigns', name: 'Campaigns' },
                    { id: 'formats', name: 'Formats only' }
                ],
                params: {
                    campaignPerformance: {
                        dateFrom: '',
                        dateTo: '',
                        status: 'all'
                    },
                    streamerStats: {
                        period: 'week',
                        minViewers: 0,
                        liveStatus: 'all'
                    },
                    formatAnalysis: {
                        dateFrom: '2026-01-01',
                        dateTo: '2026-03-01',
                        reportType: 'formats'
                    },
                    deliveryReport: {
                        campaignId: null                        
                    },
                    financialSummary: {
                        clientId: 0,
                        fiscalPeriod: 'current-month',
                        includeActive: true,
                        includeCompleted: true,
                        includePending: false
                    }
                },

                // Store report results
                reportColumns: {
                    campaignPerformance: [],
                    streamerStats: [],
                    formatAnalysis: [
                        {
                        label: 'Campaign',
                        field: 'CampaignName',
                        tdClass: 'text-f14'
                        },
                        {
                        label: 'Format',
                        field: 'FormatName',
                        tdClass: 'text-f14'
                        },
                        {
                        label: 'Views',
                        field: 'TotalViews',
                        tdClass: 'text-f12'
                        },
                        {
                        label: 'Duration',
                        field: 'DurationSum',
                        tdClass: 'text-f12'
                        },
                        {
                        label: 'Duration Time',
                        field: 'DurationTime',
                        tdClass: 'text-f12'
                        }
                    ],
                    deliveryReport: [],
                    financialSummary: []
                },
                reportResults: {
                    campaignPerformance: null,
                    streamerStats: null,
                    formatAnalysis: null,
                    deliveryReport: null,
                    deliveryReportInfo: null,
                    financialSummary: null
                }
            };
        },
        
        computed: {
            formatAnalysisColumns() {
                if (this.params.formatAnalysis.reportType === 'formats') {
                    return this.reportColumns.formatAnalysis.filter(col => col.field !== 'CampaignName');
                }
                return this.reportColumns.formatAnalysis;
            },

            barFormatAnalysisData() {
                const rows = this.reportResults.formatAnalysis;
                if (!rows || rows.length === 0) return { labels: [], datasets: [] };

                // Label: FormatName, optionally prefixed with CampaignName
                const labels = rows.map(r =>
                    this.params.formatAnalysis.reportType === 'campaigns' && r.CampaignName
                        ? `${r.CampaignName} / ${r.FormatName}`
                        : r.FormatName
                );

                return {
                    labels,
                    datasets: [
                        {
                            label: 'Views',
                            data: rows.map(r => r.TotalViews),
                            backgroundColor: '#0173bc',
                            borderColor: '#0173bc',
                            borderRadius: 4,
                            yAxisID: 'yViews'
                        },
                        {
                            label: 'Duration (s)',
                            data: rows.map(r => r.DurationSum),
                            backgroundColor: '#764ba2',
                            borderColor: '#764ba2',
                            borderRadius: 4,
                            yAxisID: 'yDuration'
                        }
                    ]
                };
            },

            barFormatAnalysisOptions() {
                return {
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                        legend: {
                            position: 'bottom',
                            display: true
                        },
                        title: {
                            display: true,
                            text: 'Format Analysis – Views & Duration'
                        }
                    },
                    scales: {
                        x: {
                            title: { display: true, text: 'Format' },
                            grid: { color: 'rgba(0, 0, 0, 0.05)' }
                        },
                        yViews: {
                            type: 'linear',
                            position: 'left',
                            title: { display: true, text: 'Views' },
                            grid: { color: 'rgba(0, 0, 0, 0.05)' }
                        },
                        yDuration: {
                            type: 'linear',
                            position: 'right',
                            title: { display: true, text: 'Duration (s)' },
                            grid: { drawOnChartArea: false }
                        }
                    }
                };
            },

            lineDeliveryData() {
                const rows = this.reportResults.deliveryReport;
                if (!rows || rows.length === 0) return { labels: [], datasets: [] };

                const labels = rows.map(r => r.CampaignDate);

                return {
                    labels,
                    datasets: [
                        {
                            label: 'Target',
                            data: rows.map(r => r.CumulativeTargetUnits),
                            borderColor: '#0173bc',
                            backgroundColor: 'rgba(1, 115, 188, 0.1)',
                            borderWidth: 2,
                            pointRadius: 3,
                            tension: 0.3,
                            fill: false
                        },
                        {
                            label: 'Actual',
                            data: rows.map(r => r.CumulativeActualUnits),
                            borderColor: '#f97316',
                            backgroundColor: 'rgba(249, 115, 22, 0.1)',
                            borderWidth: 2,
                            pointRadius: 3,
                            tension: 0.3,
                            fill: false
                        }
                    ]
                };
            },

            todayLinePlugin() {
                const rows = this.reportResults.deliveryReport;
                if (!rows || rows.length === 0) return [];

                const today = new Date().toISOString().split('T')[0];
                const todayIndex = rows.findIndex(r => r.CampaignDate === today);
                if (todayIndex === -1) return [];

                return [{
                    id: 'todayLine',
                    afterDraw(chart) {
                        const meta = chart.getDatasetMeta(0);
                        if (!meta || !meta.data[todayIndex]) return;

                        const x = meta.data[todayIndex].x;
                        const { ctx, chartArea: { top, bottom } } = chart;

                        ctx.save();
                        ctx.beginPath();
                        ctx.moveTo(x, top);
                        ctx.lineTo(x, bottom);
                        ctx.lineWidth = 2;
                        ctx.strokeStyle = 'rgba(239, 68, 68, 0.85)';
                        ctx.setLineDash([6, 4]);
                        ctx.stroke();
                        ctx.restore();

                        ctx.save();
                        ctx.fillStyle = 'rgba(239, 68, 68, 0.9)';
                        ctx.font = 'bold 11px sans-serif';
                        ctx.textAlign = 'center';
                        ctx.fillText('Today', x, top - 4);
                        ctx.restore();
                    }
                }];
            },

            lineDeliveryOptions() {
                return {
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                        legend: {
                            position: 'bottom',
                            display: true
                        },
                        title: {
                            display: true,
                            text: 'Delivery Report – Cumulative Target vs Actual'
                        }
                    },
                    scales: {
                        x: {
                            title: { display: true, text: 'Data' },
                            grid: { color: 'rgba(0, 0, 0, 0.05)' },
                            ticks: {
                                maxRotation: 45,
                                minRotation: 30
                            }
                        },
                        y: {
                            title: { display: true, text: 'Units* (cumulative)' },
                            grid: { color: 'rgba(0, 0, 0, 0.05)' },
                            beginAtZero: true
                        }
                    }
                };
            }
        },

        created() {           
            this.formatDateD = shared.formatDateD;
            this.getCamelCase = shared.getCamelCase;
            this.firstLetterCap = shared.firstLetterCap;
            
        },

        mounted() {
            // Set default dates for campaign performance
            const today = new Date();
            const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, today.getDate());
            
            this.params.campaignPerformance.dateFrom = lastMonth.toISOString().split('T')[0];
            this.params.campaignPerformance.dateTo = today.toISOString().split('T')[0];

            this.campaignDictList();
        },

        methods: {
            selectReport(reportType) {
                this.activeTab = reportType;
            },

            campaignDictList() {
                this.loader = true;
                apiService
                    .uniExec({
                        ResultName: 'campaignDict',
                        
                    })
                    .then((response) => {
                        if (response.status === 200) {
                            this.campaignDict = response.data;
                            this.selectKey++;
                        }
                    })
                    .catch((e) => shared.handleError.call(this, 'r100', true, e))
                    .finally(() => {
                        this.loader = false;
                    });
            },

            generateReport(reportType) {
                console.log('Generating report:', reportType);
                console.log('Parameters:', this.params[this.getCamelCase(reportType)]);
                
                

                const data = {
                        ResultName: 'rap'+this.firstLetterCap(this.getCamelCase(reportType)),
                        ...this.params[this.getCamelCase(reportType)]
                    };

                console.log(data);

                this.loader = true;
                apiService
                    .uniExec(data)
                    .then((response) => {
                    if (response.status === 200) {          
                        
                            console.log(response.data);

                            if (this.getCamelCase(reportType) === 'deliveryReport') {
                                this.reportResults[this.getCamelCase(reportType)] = response.data.data;
                                this.reportResults.deliveryReportInfo = response.data.info;
                            }
                            else {
                                this.reportResults[this.getCamelCase(reportType)] = response.data;
                            }
                            
                            console.log(this.reportResults);
                    }
                    })
                    .catch((e) => shared.handleError.call(this, 'r101', true, e))
                    .finally(() => {
                        this.loader = false;
                    });
            },



            refreshData() {
                console.log('Refreshing report data');
                // Clear current results
                this.reportResults = {
                    campaignPerformance: null,
                    streamerStats: null,
                    formatAnalysis: null,
                    deliveryReport: null,
                    deliveryReportInfo: null,
                    financialSummary: null
                };
            }
        },


    }
</script>



<style scoped>

/* Report Content Area */
.report-content {
    padding: 24px 0;
}



/* Report Header */
.report-header {
    margin-bottom: 32px;
    padding-bottom: 20px;
    border-bottom: 2px solid var(--border);
}

.report-title {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 12px;
}

.report-title h4 {
    margin: 0;
    font-size: 20px;
    color: var(--text-main);
    font-weight: 700;
}

.report-title .iconify {
    color: var(--orange);
}

.report-description {
    margin: 0;
    font-size: 14px;
    color: var(--text-muted);
    line-height: 1.6;
}

/* Report Parameters */
.report-parameters {
    /* background: linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%); */
    border-radius: 12px;
    padding: 24px;
    margin-bottom: 32px;
    /* border: 1px solid rgba(249, 115, 22, 0.1); */
    border: 1px solid var(--border);
    background: var(--bg-page);
}

.report-parameters .vue-select {
    width: 100%;
}

/* Generate Button */
.generate-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    width: 100%;
    padding: 14px 24px;
    background: linear-gradient(135deg, var(--orange) 0%, var(--orange-dark) 100%);
    color: white;
    border: none;
    border-radius: 8px;
    font-size: 15px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    box-shadow: 0 4px 6px rgba(249, 115, 22, 0.2);
    margin-top: 20px;
}

.generate-btn:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 12px rgba(249, 115, 22, 0.3);
}

.generate-btn:active {
    transform: translateY(0);
}

/* Report Results */
.report-results {
    background: var(--bg-card);
    border: 2px dashed var(--border);
    border-radius: 12px;
    padding: 24px;
    min-height: 300px;   
    position: relative; 
}

.chart-section {
    margin-top: 32px;
}

.results-placeholder {
    text-align: center;
    color: var(--text-muted);
}

.results-placeholder .iconify {
    color: var(--orange);
    opacity: 0.3;
    margin-bottom: 16px;
}

.results-placeholder p {
    margin: 0;
    font-size: 15px;
    font-weight: 500;
}

/* Checkbox Group */
.checkbox-group {
    display: flex;
    gap: 24px;
    flex-wrap: wrap;
    margin-top: 8px;
}

.checkbox-label {
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    font-size: 14px;
    color: var(--text-main);
    font-weight: 500;
    text-transform: none;
    letter-spacing: 0;
}

.checkbox-label input[type="checkbox"] {
    width: 18px;
    height: 18px;
    cursor: pointer;
    accent-color: var(--orange);
}

.checkbox-label span {
    user-select: none;
}

.checkbox-label:hover {
    color: var(--orange);
}

/* Report Section Header – campaign info */
.report-section-header {
    margin-bottom: 24px;
    padding: 16px 20px;
    border-radius: 10px;
    background: var(--bg-page);
    border: 1px solid var(--border);
}

.rsh-title {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 14px;
}

.rsh-title h4 {
    margin: 0;
    font-size: 17px;
    font-weight: 700;
    color: var(--text-main);
}

.rsh-title .iconify {
    color: var(--orange);
    flex-shrink: 0;
}

.campaign-status-badge {
    display: inline-block;
    padding: 3px 10px;
    border-radius: 20px;
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    background: rgba(100,100,100,0.12);
    color: var(--text-muted);
}

.campaign-status-badge.status-ongoing {
    background: rgba(34, 197, 94, 0.12);
    color: #16a34a;
}

.campaign-status-badge.status-finished {
    background: rgba(59, 130, 246, 0.12);
    color: #2563eb;
}

.campaign-status-badge.status-planned {
    background: rgba(234, 179, 8, 0.12);
    color: #b45309;
}

.campaign-status-badge.status-disabled {
    background: rgba(239, 68, 68, 0.12);
    color: #dc2626;
}

.rsh-meta {
    display: flex;
    flex-wrap: wrap;
    gap: 8px 24px;
}

.rsh-item {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 110px;
}

.rsh-label {
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: var(--text-muted);
    font-weight: 600;
}

.rsh-value {
    font-size: 14px;
    font-weight: 600;
    color: var(--text-main);
}

.unit-info {
    position: absolute;
    bottom: 2px;
    left: 16px;
   
}

/* Responsive adjustments */
@media (max-width: 768px) {
    .report-parameters {
        padding: 16px;
    }

    .checkbox-group {
        flex-direction: column;
        gap: 12px;
    }

    .tab-navigation {
        overflow-x: auto;
        overflow-y: hidden;
        -webkit-overflow-scrolling: touch;
    }

    .tab-button {
        white-space: nowrap;
    }
}

</style>